import { useMemo, useState, useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import "../styles/style_admin.css";
import { DASHBOARD_ITEM, MENU, EMPTY_FORM, DASHBOARD_DATA, ACTIONS } from "../components/admin/adminConfig";
import Sidebar from "../components/admin/Sidebar";
import AdminHeader from "../components/admin/AdminHeader";
import Dashboard from "../components/admin/Dashboard";
import ContentEditor from "../components/admin/ContentEditor";

import G_Manager_admin_Container_T1 from "../components/admin/core/G_Manager_admin_Container_T1";
import CarouselListItem from "../components/admin/carrousel/CarouselListItem";
import CarrouselEditor from "../components/admin/carrousel/CarrouselEditor";
import CarrouselPreview from "../components/admin/carrousel/CarrouselPreview";

import PartnerListItem from "../components/admin/partner/PartnerListItem";
import PartnerEditor from "../components/admin/partner/PartnerEditor";
import PartnerPreview from "../components/admin/partner/PartnerPreview"; 

import UserListItem from "../components/admin/user/UserListItem";
import UserEditor from "../components/admin/user/UserEditor";
import UserPreview from "../components/admin/user/UserPreview";

import GlobalPropertiesEditor from "../components/admin/globalProperties/GlobalPropertiesEditor";
import EnterprisePropertiesEditor from "../components/admin/globalProperties/EnterprisePropertiesEditor";
import CalendarEditor from "../components/admin/calendar/CalendarEditor";
import RoleManagementEditor from "../components/admin/role/RoleManagementEditor";

import { useAuth } from "../context/AuthContext";
import PageLoader from "../components/PageLoader";


export default function Admin() {
	const location = useLocation();
	const { user, loading } = useAuth();
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [openSections, setOpenSections] = useState(() =>
		MENU.reduce((acc, section) => ({ ...acc, [section.id]: section.id === "accueil" }), {}) // ouvrir uniquement la section "accueil" par défaut
	);
	
	const getInitialItem = () => { // Initialiser selectedItem selon le state passé du header \\
		if (!user) return DASHBOARD_ITEM;
		if (location.state?.adminItem) {
			const item = MENU.flatMap(section => section.items).find(item => item.id === location.state.adminItem);
			if (item) {
				if (user?.frontend_permissions?.[item.permission]?.access?.allowed === false) return DASHBOARD_ITEM;
				localStorage.setItem("adminSelectedItem", item.id);
				return item;
			}
		}
		// Sinon, vérifier localStorage
		const savedItem = localStorage.getItem("adminSelectedItem");
		if (savedItem) {
			const item = MENU.flatMap(section => section.items).find(item => item.id === savedItem);
			if (item) {
				if (user?.frontend_permissions?.[item.permission]?.access?.allowed === false) return DASHBOARD_ITEM;
				return item;
			}
		}
		return DASHBOARD_ITEM;
	};
	
	const [selectedItem, setSelectedItem] = useState(getInitialItem);
	const [activeAction, setActiveAction] = useState("update");
	const [formState, setFormState] = useState(EMPTY_FORM);
	const [status, setStatus] = useState(null);

	// Mettre à jour selectedItem quand le state change (même route)
	useEffect(() => {
		if (!user || !location.state?.adminItem) return;
		const item = MENU.flatMap(section => section.items).find(item => item.id === location.state.adminItem);
		if (item) {
			if (user?.frontend_permissions?.[item.permission]?.access?.allowed === false) return;
			setSelectedItem(item);
			localStorage.setItem("adminSelectedItem", item.id);
			const section = MENU.find(s => s.items.some(it => it.id === item.id));
			if (section) {
				setOpenSections(prev => ({ ...prev, [section.id]: true }));
			}
			setStatus(null);
		}
	}, [location.state?.adminItem, user]);

	// Sauvegarder dans localStorage chaque fois que selectedItem change ET ouvrir sa section
	useEffect(() => {
		if (selectedItem.id !== "dashboard") {
			localStorage.setItem("adminSelectedItem", selectedItem.id);
		} else {
			localStorage.removeItem("adminSelectedItem");
		}
		// Ouvrir automatiquement la section qui contient l'item sélectionné
		const section = MENU.find(s => s.items.some(it => it.id === selectedItem.id));
		if (section) {
			setOpenSections(prev => ({ ...prev, [section.id]: true }));
		}
	}, [selectedItem]);

	const currentSection = useMemo(
		() =>
			selectedItem.id === "dashboard" ? { id: "dashboard", label: "Accueil admin" } : MENU.find((section) => section.items.some((it) => it.id === selectedItem.id)),
		[selectedItem]
	);

	const toggleSidebar = () => {
    	setSidebarOpen((v) => !v);
  	};

	const toggleSection = (id) =>
		setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

	const handleSelect = (item) => {
		if (user?.frontend_permissions?.[item.permission]?.access?.allowed === false) return;
		setSelectedItem(item);
		setStatus(null);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setStatus({
			type: "info",
			message: `${activeAction.toUpperCase()} sur ${selectedItem.label} (API: ${selectedItem.api || "n/a"})`,
		});
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormState((prev) => ({ ...prev, [name]: value }));
	};

	const resetForm = () => setFormState(EMPTY_FORM);




	// diver gestion carousel generique \\
	const renderListItem = (props) => (
		<CarouselListItem slide={props.item} onHover={props.onHover} onEdit={props.onEdit} onDelete={props.onDelete} />
	);

	const renderEditorComponent = (props) => (
		<CarrouselEditor slide={props.item} mode={props.mode} onSave={props.onSave} onCancel={props.onCancel} onPreviewUpdate={props.onPreviewUpdate} externalImage={props.externalImage} />
	);

	const renderPreviewComponent = (props) => (
		<CarrouselPreview
			slide={props.item}
			onDropImage={props.onDropImage}
			originalAvailable={props.originalAvailable}
			showOriginal={props.showOriginal}
			onToggleOriginal={props.onToggleOriginal}
		/>
	);

	const renderListItemPartner = (props) => (
		<PartnerListItem item={props.item} onHover={props.onHover} onEdit={props.onEdit} onDelete={props.onDelete} />
	);

	const renderEditorComponentPartner = (props) => (
		<PartnerEditor item={props.item} mode={props.mode} onSave={props.onSave} onCancel={props.onCancel} onPreviewUpdate={props.onPreviewUpdate} externalImage={props.externalImage} />
	);

	const renderPreviewComponentPartner = (props) => (
		<PartnerPreview
			item={props.item}
			onDropImage={props.onDropImage}
			originalAvailable={props.originalAvailable}
			showOriginal={props.showOriginal}
			onToggleOriginal={props.onToggleOriginal}
		/>
	);

	const renderListItemUser = (props) => (
		<UserListItem item={props.item} onHover={props.onHover} onEdit={props.onEdit} onDelete={props.onDelete} />
	);

	const renderEditorComponentUser = (props) => (
		<UserEditor item={props.item} mode={props.mode} onSave={props.onSave} onCancel={props.onCancel} onPreviewUpdate={props.onPreviewUpdate} externalImage={props.externalImage} />
	);

	const renderPreviewComponentUser = (props) => (
		<UserPreview
			item={props.item}
			onDropImage={props.onDropImage}
			originalAvailable={props.originalAvailable}
			showOriginal={props.showOriginal}
			onToggleOriginal={props.onToggleOriginal}
		/>
	);


	if (loading) return <PageLoader label="Chargement de l'espace admin" />;
	if (!user) return <Navigate to="/auth" state={{ defaultForm: "signin" }} replace />;

	return (
		<div className="admin-shell">
			<Sidebar
				sidebarOpen={sidebarOpen}
				toggleSidebar={toggleSidebar}
				selectedItem={selectedItem}
				openSections={openSections}
				toggleSection={toggleSection}
				handleSelect={handleSelect}
			/>

			<main className="admin-main">
				<AdminHeader currentSection={currentSection} selectedItem={selectedItem} activeAction={activeAction} setActiveAction={setActiveAction} />
				{selectedItem.id === "dashboard" ? (
					<Dashboard data={DASHBOARD_DATA} />
				) : selectedItem.id === "carrousel" ? (
					<G_Manager_admin_Container_T1
						key="carrousel-manager"
						apiEndpoint="carrousel"
						pkey="id"
						ListItemComponent={renderListItem}
						EditorComponent={renderEditorComponent}
						PreviewComponent={renderPreviewComponent}
					/>
				) : selectedItem.id === "partner" ? (
					<G_Manager_admin_Container_T1
						key="partner-manager"
						apiEndpoint="partner"
						pkey="id"
						ListItemComponent={renderListItemPartner}
						EditorComponent={renderEditorComponentPartner}
						PreviewComponent={renderPreviewComponentPartner}
					/>
				) : selectedItem.id === "users" ? (
					<G_Manager_admin_Container_T1
						key="users-manager"
						apiEndpoint="user"
						pkey="id"
						ListItemComponent={renderListItemUser}
						EditorComponent={renderEditorComponentUser}
						PreviewComponent={renderPreviewComponentUser}
					/>
				) : selectedItem.id === "confGeneral" ? (
					<EnterprisePropertiesEditor />
				) : selectedItem.id === "divers" ? (
					<GlobalPropertiesEditor />
				) : selectedItem.id === "calendrier-ouverture" ? (
					<CalendarEditor />
				) : selectedItem.id === "roles" ? (
					<RoleManagementEditor />
				) : (
					<ContentEditor
						selectedItem={selectedItem}
						activeAction={activeAction}
						formState={formState}
						handleChange={handleChange}
						handleSubmit={handleSubmit}
						resetForm={resetForm}
						status={status}
					/>
				)}
			</main>
		</div>
	);
}